import { zodResolver } from '@hookform/resolvers/zod';
import {
  Action,
  Alert,
  FloatingSave,
  Page,
  SimpleSelect,
} from 'corso-admin-ui';
import {
  isTruthy,
  ShipmentTrackingEventProvider,
  shipmentTrackingEventProviderName,
} from 'corso-types';
import { FormEventHandler, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  useConfigSettings,
  useConfigSettingsUpdate,
} from '~/hooks/useConfigSettings';
import useIsCorsoAdmin from '~/hooks/useIsCorsoAdmin';
import { trackingSettingsFormSchema } from '~/types';
import SettingsAnchorCard from '../components/SettingsAnchorCard';

const trackingSourceOptions = Object.values(ShipmentTrackingEventProvider).map(
  (value) => ({ label: shipmentTrackingEventProviderName[value], value }),
);

export default function TrackingSettingsPage() {
  const formId = 'tracking-settings';
  const formRef = useRef<HTMLFormElement>(null);

  const { data } = useConfigSettings(({ tracking }) => tracking);
  const { mutateAsync: saveChanges } = useConfigSettingsUpdate();

  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(trackingSettingsFormSchema),
    values: data,
  });

  const submitHandler: FormEventHandler = (event) => {
    handleSubmit((values) => saveChanges({ tracking: values }))(event).catch(
      console.error,
    );
  };

  const isTrackingEnabled = watch('isTrackingEnabled');

  const isCorsoAdmin = useIsCorsoAdmin();

  return (
    <Page
      title="Tracking"
      secondaryActions={[
        isTrackingEnabled && {
          id: 'disable-tracking',
          content: 'Turn Off',
          onAction: () => {
            setValue('isTrackingEnabled', false, { shouldDirty: true });
            formRef.current?.requestSubmit();
          },
        },
      ].filter(isTruthy)}
      primaryAction={
        isTrackingEnabled
          ? undefined
          : {
              onAction: () => {
                setValue('isTrackingEnabled', true, { shouldDirty: true });
                formRef.current?.requestSubmit();
              },
              content: 'Turn On',
            }
      }
    >
      <form
        id={formId}
        ref={formRef}
        className="flex flex-col gap-4"
        onSubmit={submitHandler}
        onReset={(e) => {
          e.preventDefault();
          reset();
        }}
      >
        {!isTrackingEnabled && (
          <Alert
            elevated
            variant="info"
            heading="Tracking is Disabled. Settings can be configured, but tracking will not be shown on the Customer Portal."
          />
        )}
        <FloatingSave
          isDirty={isDirty}
          isSubmitting={isSubmitting}
          form={formId}
        />{' '}
        <Alert variant="tip" heading="Learn More About Corso Tracking" elevated>
          See which carriers are supported{' '}
          <Action
            variant="link"
            to="https://help.corso.com/tracking/intro-to-tracking"
            target="_blank"
            rel="noreferrer"
          >
            here.
          </Action>
        </Alert>
        <SettingsAnchorCard id="tracking">
          <Controller
            control={control}
            name="trackingEventProvider"
            render={({ field: { onChange, value }, fieldState }) => (
              <SimpleSelect
                label="Order Fulfillment Tracking Source"
                details="The source to use when sending tracking updates to Klaviyo and other third party providers. Please contact us to change this setting."
                options={trackingSourceOptions}
                value={value}
                onChange={onChange}
                disabled={!isCorsoAdmin}
                error={fieldState.error?.message}
              />
            )}
          />
        </SettingsAnchorCard>
      </form>
    </Page>
  );
}
